/* import __COLOCATED_TEMPLATE__ from './consent.hbs'; */
/* RESPONSIBLE TEAM: team-knowledge-foundations */
import type Store from '@ember-data/store';
import { action } from '@ember/object';
import { inject as service } from '@ember/service';
import Component from '@glimmer/component';
import { tracked } from '@glimmer/tracking';
import type SupportedLocale from 'embercom/models/articles/site/supported-locale';
import { CategoryTypes } from 'embercom/models/data/consent-banners/category-types';
import { SettingTypes } from 'embercom/models/data/consent-banners/setting-types';
import type HelpCenterSite from 'embercom/models/help-center-site';
import type IntlService from 'embercom/services/intl';

interface Args {
  site: HelpCenterSite;
}

export default class Consent extends Component<Args> {
  @service declare intl: IntlService;
  @service declare helpCenterService: any;
  @service declare notificationsService: any;
  @service declare store: Store;

  categoryTypes = Object.values(CategoryTypes);
  bannerTypes = Object.values(SettingTypes);

  @tracked
  banner = this.args.site.consentBanner;
  @tracked
  selectedLocale = this.args.site.locale;
  @tracked
  selectedSetting = this.banner?.settings.find((setting) => setting.isGdpr);

  @action
  toggleShowBanner() {
    if (!this.banner) {
      this._createBannerInMemory();
    } else {
      this.banner.showBanner = !this.banner.showBanner;
    }
  }

  @action
  selectBannerType(type: string) {
    // Find the existing setting (all settings are created in _createBannerInMemory)
    let setting = this.banner.settings.find((setting) => setting.bannerType === type);
    this.selectedSetting = setting;
  }

  @action
  selectLocale(locale: string) {
    this.selectedLocale = locale;
  }

  @action
  async updateCopyText(text: string) {
    let copy = this.copy ?? this.createCopy();
    copy.text = text;
  }

  get showBanner() {
    return this.banner?.showBanner ?? false;
  }

  get copy() {
    return this.selectedSetting?.copies?.find((copy) => copy.locale === this.selectedLocale);
  }

  get bannerTypeOptions(): Array<any> {
    return this.bannerTypes.map((type) => ({
      text: this.intl.t(`articles.settings.redesign.general.consent.type.banner.${type}`),
      value: type,
      component: 'articles/site/settings/general/banner-type-dropdown-item',
      componentShouldReplaceItem: false,
      componentAttrs: {
        text: this.intl.t(`articles.settings.redesign.general.consent.type.banner.${type}`),
        value: type,
        description: this.intl.t(
          `articles.settings.redesign.general.consent.type.description.${type}`,
        ),
      },
    }));
  }

  get selectedBannerTypeText(): string {
    if (this.selectedSetting?.bannerType) {
      return this.intl.t(
        `articles.settings.redesign.general.consent.type.banner.${this.selectedSetting.bannerType}`,
      );
    }
    return '';
  }

  get localeOptions(): Array<any> {
    return this.args.site.selectedLocalesOrdered.map((locale: SupportedLocale) => ({
      text: locale.name,
      value: locale.localeId,
    }));
  }

  _createBannerInMemory() {
    // Create category for GDPR setting (backend provides app_id and relationships)
    let category = this.store.createRecord('consent-banners/category', {
      necessary: true,
      functional: false,
      advertisement: false,
      performance: false,
    });

    // Create GDPR setting with category
    let gdprSetting = this.store.createRecord('consent-banners/setting', {
      bannerType: SettingTypes.GDPR,
      shouldFreeze: false,
      category,
    });

    // Create CCPA setting
    let ccpaSetting = this.store.createRecord('consent-banners/setting', {
      bannerType: SettingTypes.CCPA,
      shouldFreeze: false,
    });

    // Create Simple setting
    let simpleSetting = this.store.createRecord('consent-banners/setting', {
      bannerType: SettingTypes.Simple,
      shouldFreeze: false,
    });

    // Create banner with all three settings
    let newBanner = this.store.createRecord('consent-banners/banner', {
      showBanner: true,
      settings: [gdprSetting, ccpaSetting, simpleSetting],
    });

    // Set banner references without saving
    this.args.site.consentBanner = newBanner;
    this.banner = newBanner;
    this.selectedSetting = gdprSetting;
  }

  createCopy() {
    let copy = this.store.createRecord('consent-banners/copy');
    copy.setProperties({
      text: '',
      locale: this.selectedLocale,
      // Backend provides app_id and consent_banner_setting_id automatically
    });
    this.selectedSetting?.copies!.pushObject(copy);
    return copy;
  }
}

declare module '@glint/environment-ember-loose/registry' {
  export default interface Registry {
    'Articles::Site::Settings::General::Consent': typeof Consent;
    'articles/site/settings/general/consent': typeof Consent;
  }
}
